import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FlaskConical, ShieldCheck, Snowflake, Dna, Truck, PackageCheck, MapPin, Headphones } from "lucide-react";
import { api } from "../lib/api";
import { LabVerified, HowItWorks, Testimonials } from "../components/HomeExtras";
import { Vial } from "../components/Vial";
import { Marquee } from "../components/Marquee";
import { ProductCard } from "../components/ProductCard";

const HEADLINE = ["CELLULAR PRECISION.", "UNCOMPROMISED", "LONGEVITY."];

const CHAPTERS = [
  { n: "01", title: "Cellular Signaling & Rejuvenation", text: "Peptides act as precise biological messengers, docking into cell receptors to trigger tissue repair, mitochondrial biogenesis, and protein synthesis." },
  { n: "02", title: "Targeted Molecular Efficacy", text: "Bio-engineered peptide sequences hit specific metabolic targets with micro-molar precision, unlike broad-spectrum supplements." },
  { n: "03", title: "Sustained Neuro & Physical Longevity", text: "By stimulating endogenous hormones and growth factors, Alpha Peps protocols support vital organs, joints, brain health, and metabolic elasticity." },
];

const Hero = () => {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  return (
    <section
      className="spotlight relative overflow-hidden px-5 pb-24 pt-36 sm:px-8 lg:pt-44"
      onMouseMove={(e) => {
        const { innerWidth, innerHeight } = window;
        setTilt({ x: (e.clientY / innerHeight - 0.5) * -8, y: (e.clientX / innerWidth - 0.5) * 10 });
      }}
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-[#6E7683]/20 blur-[140px]" />
      <div className="mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#6E7683]/40 bg-[#111114]/70 px-4 py-1.5 font-mono text-[10px] tracking-[0.3em] text-[#E8E9EB]"
            data-testid="hero-eyebrow"
          >
            <Dna className="h-3 w-3" /> BIO-ENGINEERED PEPTIDE THERAPEUTICS
          </motion.div>

          <h1 className="font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            {HEADLINE.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.2 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className={`block ${i === 2 ? "text-glow text-[#E8E9EB]" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
            data-testid="hero-subheadline"
          >
            Pharmaceutical-grade research peptides synthesized for optimal recovery, metabolic mastery, and
            neuro-cognitive enhancement.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/products"
              data-testid="hero-cta-products"
              className="group inline-flex items-center gap-2 rounded-full bg-[#6E7683] px-7 py-3.5 font-mono text-xs tracking-[0.2em] text-white transition-colors duration-200 hover:bg-[#E8E9EB] hover:text-[#050811]"
            >
              EXPLORE PROTOCOLS
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/benefits"
              data-testid="hero-cta-benefits"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-mono text-xs tracking-[0.2em] text-slate-300 transition-colors duration-200 hover:border-[#E8E9EB]/60 hover:text-white"
            >
              READ HEALTH MANIFESTO
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden justify-center lg:flex"
          style={{ transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`, transition: "transform 0.4s ease-out" }}
          data-testid="hero-visual"
        >
          <div className="absolute top-0 h-40 w-40 rounded-full bg-[#E8E9EB]/25 blur-[80px]" />
          <Vial code="AP-01" pid="retatrutide" className="animate-floaty w-full max-w-md" />
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            className="glass absolute -left-6 top-10 flex items-center gap-2 rounded-xl px-4 py-2.5"
          >
            <FlaskConical className="h-4 w-4 text-[#E8E9EB]" />
            <span className="font-mono text-[10px] tracking-[0.15em] text-slate-300">HPLC 99.9%</span>
          </motion.div>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
            className="glass absolute -right-4 top-1/2 flex items-center gap-2 rounded-xl px-4 py-2.5"
          >
            <ShieldCheck className="h-4 w-4 text-[#E8E9EB]" />
            <span className="font-mono text-[10px] tracking-[0.15em] text-slate-300">ISO 9001 LAB</span>
          </motion.div>
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.1 }}
            className="glass absolute -left-2 bottom-8 flex items-center gap-2 rounded-xl px-4 py-2.5"
          >
            <Snowflake className="h-4 w-4 text-[#E8E9EB]" />
            <span className="font-mono text-[10px] tracking-[0.15em] text-slate-300">COLD-CHAIN</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const BADGES = [
  { icon: Truck, title: "Fast Shipping", text: "Dispatch within 24 hours via tracked cold-chain courier." },
  { icon: PackageCheck, title: "Secure Packaging", text: "Discreet, temperature-protected packaging on every order." },
  { icon: MapPin, title: "Australia Wide", text: "Express delivery to every state and territory across Australia." },
  { icon: Headphones, title: "Support Available", text: "Real human support with under 2 business hour responses." },
];

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />

      <section className="mx-auto max-w-7xl px-5 pt-20 sm:px-8" data-testid="trust-badges">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {BADGES.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="glass rounded-2xl p-6 transition-colors duration-300 hover:border-[#E8E9EB]/30"
              data-testid={`trust-badge-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <b.icon className="h-6 w-6 text-[#E8E9EB]" />
              <h3 className="mt-4 font-display text-lg font-semibold text-white">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{b.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="featured-protocols">
        <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">CATALOG</div>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Research Protocols &<br />Peptide Formulations
            </h2>
          </div>
          <Link to="/products" data-testid="view-all-products-link" className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-slate-400 transition-colors duration-200 hover:text-[#E8E9EB]">
            VIEW ALL FORMULATIONS
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      <section className="border-t border-white/5 bg-[#0A0A0C] px-5 py-24 sm:px-8 lg:py-32" data-testid="manifesto-teaser">
        <div className="mx-auto max-w-7xl">
          <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">THE MANIFESTO</div>
          <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Why peptides are rewriting human performance
          </h2>
          <div className="mt-16 flex flex-col gap-0">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group grid gap-4 border-t border-white/8 py-10 transition-colors duration-300 hover:border-[#E8E9EB]/30 md:grid-cols-[120px_1fr_1.4fr] md:items-baseline"
                data-testid={`manifesto-chapter-${c.n}`}
              >
                <span className="font-mono text-sm text-[#6E7683] transition-colors duration-300 group-hover:text-[#E8E9EB]">/{c.n}</span>
                <h3 className="font-display text-xl font-semibold tracking-tight text-white sm:text-2xl">{c.title}</h3>
                <p className="text-sm leading-relaxed text-slate-400 sm:text-base">{c.text}</p>
              </motion.div>
            ))}
          </div>
          <Link to="/benefits" data-testid="manifesto-read-more" className="group mt-8 inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-[#E8E9EB]">
            READ THE FULL HEALTH BENEFITS
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <LabVerified />
      <Testimonials />
      <HowItWorks />

      <section className="relative overflow-hidden px-5 py-24 text-center sm:px-8 lg:py-32" data-testid="cta-band">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[400px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#6E7683]/15 blur-[120px]" />
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Begin your <span className="text-glow text-[#E8E9EB]">protocol</span>
          </h2>
          <p className="mx-auto mt-4 max-w-md text-sm text-slate-400 sm:text-base">
            Every batch ships with a verifiable Certificate of Analysis and cold-chain delivery.
          </p>
          <Link
            to="/products"
            data-testid="cta-band-button"
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#6E7683] px-8 py-4 font-mono text-xs tracking-[0.25em] text-white transition-colors duration-200 hover:bg-[#E8E9EB] hover:text-[#050811]"
          >
            SHOP FORMULATIONS <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
